import React, { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Link, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { AlternateEmail, MarkEmailReadOutlined } from '@mui/icons-material';
import { emailService } from '../../services/emailService';
import { useAutoFetch } from '../../hooks/useAutoFetch';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import useTimezone from '../../hooks/useTimezone';
import type { EmailLogEntry } from '../../types/email';
import EmptyState from '../common/EmptyState';
import StatusChip from '../common/StatusChip';
import ResendEmailButton from './ResendEmailButton';
import { useRefreshWhileSending } from './useRefreshWhileSending';

interface Props {
  /** The record kind the backend knows its emails by, e.g. 'admission_application'. */
  entityType: string;
  entityId: string;
  /** May send this record's emails again (write on its module). */
  canResend: boolean;
  /** How staff give the record an address, said when it has none. */
  missingAddress?: string;
}

const EmailItem: React.FC<{ log: EmailLogEntry; canResend: boolean; onResent: () => void }> = React.memo(({ log, canResend, onResent }) => {
  const { formatDateTime } = useTimezone();
  const when = log.status === 'sent' && log.sentAt ? `Sent ${formatDateTime(log.sentAt)}` : formatDateTime(log.createdAt);
  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px', px: 2, py: 1.5 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>{log.typeLabel}</Typography>
          <Typography variant="body2" color="text.secondary" noWrap title={log.subject}>{log.subject}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            To {log.to.join(', ')} · {when}{log.sentBy ? ` · ${log.sentBy.name}` : ''}
          </Typography>
          {log.status === 'failed' && log.error && (
            <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.25 }}>{log.error}</Typography>
          )}
        </Box>
        <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
          <StatusChip status={{ kind: 'email', value: log.status }} size="small" />
          {canResend && <ResendEmailButton log={log} onResent={onResent} />}
        </Stack>
      </Stack>
    </Paper>
  );
});

/** Every email sent about one record — what went, to whom, and whether it arrived at the mail server. */
const RecordEmails: React.FC<Props> = ({ entityType, entityId, canResend, missingAddress = 'Add an email address to the record to start.' }) => {
  const { canRead } = useModulePermissions();
  const { data, loading, error, retry } = useAutoFetch(`${entityType}:${entityId}`, () => emailService.forRecord(entityType, entityId));
  const refresh = useCallback(() => retry(), [retry]);
  useRefreshWhileSending(data?.items, refresh);

  if (!data) {
    if (error) return <Alert severity="error">{error}</Alert>;
    return (
      <Stack spacing={1.5}>
        {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={84} sx={{ borderRadius: '12px' }} />)}
      </Stack>
    );
  }

  const setupLink = canRead('email')
    ? <Link component={RouterLink} to="/app/email/setup">Email setup</Link>
    : 'Email setup';

  return (
    <Stack spacing={2} sx={{ opacity: loading ? 0.7 : 1, transition: 'opacity 150ms' }}>
      {data.recipients.length > 0 ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
          <AlternateEmail sx={{ fontSize: 18 }} />
          <Typography variant="body2">
            Emails go to <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{data.recipients.join(', ')}</Box>
          </Typography>
        </Stack>
      ) : (
        <Alert severity="info" variant="outlined" sx={{ borderRadius: '12px' }}>
          There’s no email address on file, so nothing is emailed. {missingAddress}
        </Alert>
      )}

      {data.items.length === 0 ? (
        <EmptyState
          dense
          icon={<MarkEmailReadOutlined />}
          title="No emails yet"
          description={<>Emails switched on in {setupLink} show here once sent. Staff always see who gets one before it goes.</>}
        />
      ) : (
        <Stack spacing={1.25}>
          {data.items.map(log => <EmailItem key={log._id} log={log} canResend={canResend} onResent={refresh} />)}
        </Stack>
      )}
    </Stack>
  );
};

export default RecordEmails;
